import { CommentIcon, LockIcon } from '@/components/icons'
import { Panel } from '@/components/ui'

/**
 * 评论区（设计稿 D2 底部 / M4 底部）。
 *
 * TODO(backend): 关联 docs/backend-sync/2026-08-07-post-detail-diff.md
 *
 * **后端明确不做评论。** openapi.yaml 的开篇就写着：「评论功能不存在：没有表、
 * 没有路由，`/comments` 返回普通 404。只保留恒为 `false` 的 `comments_enabled`
 * 与恒为 `0` 的 `comment_count`。」
 *
 * 于是这里有个取舍。选项是：
 *   (a) 完全不渲染评论区 —— 与设计稿不符，评审时看不到这块设计；
 *   (b) 渲染一个能输入、能提交的评论框 —— **不可接受**：提交必然 404，
 *       而且会让人以为后端有这个功能；
 *   (c) 按设计稿渲染视觉，但明确标注「未开放」，输入区不可交互。
 *
 * 取 (c)：由 `commentsEnabled` 驱动。它来自后端的 `comments_enabled`，
 * 当前恒为 false；哪天后端真做了评论，这个组件会自动切到可用形态。
 *
 * 这里原本还有两条开发期「版式示例评论」，已随 Mock 一起删除：对接真实后端后，
 * 任何位置都不再显示编造的内容。评论列表要等后端真的有 `/comments` 才会出现。
 */

export interface CommentSectionProps {
  /** 来自 `PublicPostDetail.comments_enabled`，首期恒为 false。 */
  commentsEnabled: boolean
  /** 来自 `PublicPostDetail.comment_count`，首期恒为 0。 */
  commentCount: number
  /** 是否已登录 —— 决定是显示「登录后发表评论」还是输入框。 */
  isSignedIn: boolean
}

export function CommentSection({
  commentsEnabled,
  commentCount,
  isSignedIn,
}: CommentSectionProps) {
  return (
    <section aria-labelledby="comments-heading" className="space-y-3">
      <div className="flex items-center gap-2">
        <CommentIcon className="size-[1.125rem] text-primary" />
        <h2 id="comments-heading" className="text-base font-semibold text-foreground sm:text-lg">
          评论
        </h2>
        <span className="text-sm text-muted-foreground">{commentCount}</span>
        {!commentsEnabled && (
          <span className="ml-1 rounded-md bg-surface-muted px-2 py-0.5 text-xs text-muted-foreground">
            未开放
          </span>
        )}
      </div>

      <Panel className="space-y-3">
        {/* 输入区 */}
        {commentsEnabled ? (
          isSignedIn ? (
            <textarea
              rows={3}
              placeholder="写下你的评论…"
              className="w-full rounded-card border border-border bg-surface px-3 py-2.5 text-sm text-foreground placeholder:text-subtle-foreground focus:border-primary"
            />
          ) : (
            <p className="flex items-center justify-center gap-2 whitespace-nowrap rounded-card border border-dashed border-border py-4 text-sm text-muted-foreground">
              <LockIcon className="size-4" />
              登录后发表评论
            </p>
          )
        ) : (
          /*
            设计稿画的是「🔒 登录后发表评论」。功能未开放时照这个版式渲染，
            但文案改成事实描述 —— 说「登录后可发表」在功能不存在时是误导。
          */
          <p className="flex items-center justify-center gap-2 whitespace-nowrap rounded-card border border-dashed border-border py-4 text-sm text-muted-foreground">
            <LockIcon className="size-4" />
            评论功能尚未开放
          </p>
        )}
      </Panel>
    </section>
  )
}
